import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";

import { deleteAdmin } from "../api/adminsApi";
import type { Admin } from "../types";

type DeleteAdminModalProps = {
    isOpen: boolean;
    onClose: () => void;
    admin: Admin | null;
    onCompleted?: (message: string) => void;
};

export const DeleteAdminModal = ({
    isOpen,
    onClose,
    admin,
    onCompleted,
}: DeleteAdminModalProps) => {
    const queryClient = useQueryClient();
    const [reason, setReason] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!isOpen) {
            setReason("");
            setError("");
        }
    }, [isOpen]);

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!admin) {
                throw new Error("No admin selected for deletion.");
            }

            return deleteAdmin(admin.adminId, {
                reason: reason.trim() || undefined,
            });
        },
        onSuccess: async (response) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["admins"] }),
                admin
                    ? queryClient.removeQueries({
                          queryKey: ["adminDetails", admin.adminId],
                      })
                    : Promise.resolve(),
            ]);
            onClose();
            setReason("");
            setError("");
            onCompleted?.(response.message || "Admin deleted successfully.");
        },
        onError: (mutationError) => {
            setError(extractApiError(mutationError));
        },
    });

    const handleClose = () => {
        if (deleteMutation.isPending) return;
        onClose();
        setReason("");
        setError("");
    };

    if (!admin) {
        return null;
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
                                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-800">
                                    <div>
                                        <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-white">
                                            Delete Admin Account
                                        </Dialog.Title>
                                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                            {admin.fullName} ({admin.email})
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="space-y-4 px-6 py-5">
                                    <div className="flex gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
                                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                        <p>
                                            This action permanently deletes the admin account and revokes
                                            active sessions. It cannot be undone.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Reason <span className="text-xs text-slate-400">(optional)</span>
                                        </label>
                                        <textarea
                                            rows={4}
                                            value={reason}
                                            onChange={(event) => {
                                                setReason(event.target.value);
                                                if (error) setError("");
                                            }}
                                            placeholder="Why is this admin account being removed?"
                                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                                        />
                                    </div>

                                    {error ? (
                                        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
                                            {error}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="flex gap-3 border-t border-slate-200 px-6 py-5 dark:border-slate-800">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleClose}
                                        disabled={deleteMutation.isPending}
                                        className="flex-1"
                                    >
                                        Cancel
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={() => deleteMutation.mutate()}
                                        disabled={deleteMutation.isPending}
                                        className="flex flex-1 items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-65"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                        {deleteMutation.isPending ? "Deleting..." : "Delete Admin"}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
